import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Attach-existing-task modal: verify reference code before allowing save,
// show a green check when the code resolves to an attachable task.
export default class extends Controller {
  static targets = ["select", "code", "okIcon", "spinner", "hint", "matchHint", "save"]
  static values = {
    lookupUrl: String,
    notFound: String,
    notAttachable: String,
    verifiedCode: { type: String, default: "" }
  }
  static classes = ["hidden", "invalid"]

  connect() {
    this._debounceTimer = null
    this._abortController = null
    this.syncSaveButton()
  }

  disconnect() {
    clearTimeout(this._debounceTimer)
    this._abortController?.abort()
  }

  selectChanged() {
    if (this.selectTarget.value) {
      this.codeTarget.value = ""
      this.clearCodeStatus()
    }
    this.syncSaveButton()
  }

  codeInput() {
    if (this.codeTarget.value.trim()) {
      this.selectTarget.value = ""
    }
    this.verifiedCodeValue = ""
    this.hide(this.okIconTarget)
    this.hide(this.matchHintTarget)
    this.hide(this.hintTarget)
    this.codeTarget.classList.remove(this.invalidClass)
    this.syncSaveButton()

    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this.lookup(), 350)
  }

  codeBlur() {
    clearTimeout(this._debounceTimer)
    this.lookup()
  }

  guardSubmit(event) {
    if (this.canSave) return
    event.preventDefault()
    event.stopPropagation()
    if (this.codeTarget.value.trim() && !this.verifiedCodeValue) {
      this.lookup()
    }
  }

  async lookup() {
    const code = this.codeTarget.value.trim()
    if (!code) {
      this.clearCodeStatus()
      this.syncSaveButton()
      return
    }

    this._abortController?.abort()
    this._abortController = new AbortController()

    this.show(this.spinnerTarget)
    this.hide(this.okIconTarget)
    this.hide(this.hintTarget)
    this.hide(this.matchHintTarget)

    try {
      const url = new URL(this.lookupUrlValue, window.location.origin)
      url.searchParams.set("reference_code", code)

      const response = await get(url.toString(), {
        responseKind: "json",
        signal: this._abortController.signal
      })
      if (!response.ok) throw new Error("lookup failed")

      const data = await response.json
      if (this.codeTarget.value.trim().toLowerCase() !== code.toLowerCase()) return

      if (!data.found) {
        this.verifiedCodeValue = ""
        this.hide(this.okIconTarget)
        this.showHint(this.notFoundValue)
      } else if (!data.attachable) {
        this.verifiedCodeValue = ""
        this.hide(this.okIconTarget)
        this.showHint(data.error || this.notAttachableValue)
      } else {
        this.verifiedCodeValue = code
        this.show(this.okIconTarget)
        this.codeTarget.classList.remove(this.invalidClass)
        this.matchHintTarget.textContent = data.name || ""
        this.show(this.matchHintTarget)
      }
    } catch (err) {
      if (err.name === "AbortError") return
      this.verifiedCodeValue = ""
      this.showHint(this.notFoundValue)
    } finally {
      this.hide(this.spinnerTarget)
      this.syncSaveButton()
    }
  }

  get canSave() {
    if (this.selectTarget.value) return true
    const code = this.codeTarget.value.trim()
    return Boolean(
      code &&
      this.verifiedCodeValue &&
      this.verifiedCodeValue.toLowerCase() === code.toLowerCase()
    )
  }

  syncSaveButton() {
    if (!this.hasSaveTarget) return
    this.saveTarget.disabled = !this.canSave
  }

  clearCodeStatus() {
    this.verifiedCodeValue = ""
    this.hide(this.okIconTarget)
    this.hide(this.spinnerTarget)
    this.hide(this.hintTarget)
    this.hide(this.matchHintTarget)
    this.codeTarget.classList.remove(this.invalidClass)
  }

  showHint(message) {
    this.hintTarget.textContent = message
    this.show(this.hintTarget)
    this.codeTarget.classList.add(this.invalidClass)
  }

  show(el) {
    el.classList.remove(this.hiddenClass)
  }

  hide(el) {
    el.classList.add(this.hiddenClass)
  }
}
